import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import fileIcon from "../../../assets/images/icon/fileIcon.svg";
import crossIcon from "../../../assets/images/icon/x.png";
import { useTranslation } from "react-i18next";
import {
  translationConstants,
  allowedFileTypes,
  allowedImageTypes,
} from "../../../constants";

const FileUploader = ({
  onFileUpload,
  sm,
  single,
  defaultValue,
  disabled,
  isImage,
  isError,
  isAllowPreview,
  setValidationErrorMessage,
  onChangeFlag,
  customColor,
  onFileRemove,
}) => {
  const { t } = useTranslation();

  const [selectedFiles, setSelectedFiles] = useState([]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      let filesExceedingSizeLimit = acceptedFiles.filter(
        (file) => file.size > 10 * 1024 * 1024
      );

      let invalidFiles = acceptedFiles.filter((file) => {
        const fileType = file.name.split(".").pop();
        return (
          (isImage && !allowedImageTypes.includes(`.${fileType}`)) ||
          (!isImage && !allowedFileTypes.includes(`.${fileType}`))
        );
      });

      if (filesExceedingSizeLimit.length > 0) {
        //At least one file exceeds the size limit, show toast message
        filesExceedingSizeLimit.forEach((file) => {
          setValidationErrorMessage(
            t(translationConstants.FILE_SIZE_LIMIT_ERROR)
          );
        });
      } else if (invalidFiles.length > 0) {
        //Invalid files, show error message
        if (isImage) {
          setValidationErrorMessage(
            t(translationConstants.IMAGE_TYPE_NOT_ALLOWED)
          );
        } else {
          setValidationErrorMessage(
            t(translationConstants.FILE_TYPE_NOT_ALLOWED)
          );
        }
      } else {
        //All files are within the size limit and valid, proceed with upload
        setValidationErrorMessage("");
        setSelectedFiles((prevFiles) => {
          let files = single ? acceptedFiles : [...prevFiles, ...acceptedFiles];
          if (onFileUpload) {
            onFileUpload(files);
            onChangeFlag && onChangeFlag(true);
          }
          return files;
        });
      }
    },
    // eslint-disable-next-line
    [onFileUpload]
  );

  const removeFile = (file, event) => {
    event.stopPropagation();
    setSelectedFiles((prevFiles) => {
      const newFiles = prevFiles.filter((f) => f !== file);
      if (onFileUpload) {
        onFileUpload(newFiles);
        onChangeFlag && onChangeFlag(true);
      }
      return newFiles;
    });
    if (onFileRemove) {
      onFileRemove();
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: isImage
      ? {
          "image/*": [".jpeg", ".jpg", ".png", ".jfif"],
        }
      : [".pdf", ".jpeg", ".jpg", ".png", ".jfif"],
    multiple: !single,
    disabled: disabled,
    noClick: disabled,
  });

  useEffect(() => {
    if (defaultValue) setSelectedFiles(defaultValue);
    // eslint-disable-next-line
  }, [defaultValue]);

  return (
    <>
      <div
        {...getRootProps()}
        className={`dropzone${sm ? "-sm" : ""}${
          isAllowPreview ? " d-none" : ""
        }${isError ? " dotted-border" : ""}${
          disabled ? " dropzone-disabled" : ""
        } ${customColor}`}
        data-toggle="tooltip"
        data-placement="top"
        title={
          single
            ? t(translationConstants.dragDropClickSelectFile)
            : t(translationConstants.dragDropClickSelectFiles)
        }
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>{t(translationConstants.DROP_THE_FILE_HERE)}</p>
        ) : (
          <div className="d-flex justify-content-center align-items-center">
            <div className="iconDivPlus">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
              >
                <path
                  d="M10.8335 4.66665C10.8335 4.20641 10.4604 3.83331 10.0002 3.83331C9.53993 3.83331 9.16683 4.20641 9.16683 4.66665V9.66665H4.16683C3.70659 9.66665 3.3335 10.0397 3.3335 10.5C3.3335 10.9602 3.70659 11.3333 4.16683 11.3333H9.16683V16.3333C9.16683 16.7935 9.53993 17.1666 10.0002 17.1666C10.4604 17.1666 10.8335 16.7935 10.8335 16.3333V11.3333H15.8335C16.2937 11.3333 16.6668 10.9602 16.6668 10.5C16.6668 10.0397 16.2937 9.66665 15.8335 9.66665H10.8335V4.66665Z"
                  fill="white"
                />
              </svg>
            </div>
            <p className="px-2">
              {single
                ? t(translationConstants.dragDropClickSelectFile)
                : t(translationConstants.dragDropClickSelectFiles)}
            </p>
          </div>
        )}
      </div>
      <ul className="FileNameCross">
        {selectedFiles.map((file, index) => (
          <li
            key={index}
            disabled={disabled}
            className={`${isAllowPreview ? "mt-2" : ""}`}
          >
            <img className="fileIcon " src={fileIcon} alt="" />
            <p>{file?.name || file?.originalFileName}</p>
            {isAllowPreview ? (
              <a
                href={file?.link}
                target="_blank"
                rel="noreferrer"
                className="d-flex align-items-center"
              >
                <svg
                  width="20"
                  height="20"
                  // viewBox="0 0 50 16"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.1685 8.99922C16.1685 8.99922 14.0341 13.268 9.05391 13.268C4.07371 13.268 1.93933 8.99922 1.93933 8.99922C1.93933 8.99922 4.07371 4.73047 9.05391 4.73047C14.0341 4.73047 16.1685 8.99922 16.1685 8.99922Z"
                    stroke="#588930"
                    strokeWidth="1.42292"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="9.05381"
                    cy="8.99766"
                    r="2.13438"
                    stroke="#588930"
                    strokeWidth="1.42292"
                    strokeLinecap="round"
                  />
                </svg>
              </a>
            ) : (
              <img
                className={`crossIcon ${disabled ? "cursor-not-allowed" : ""}`}
                onClick={(e) => !disabled && removeFile(file, e)}
                src={crossIcon}
                alt=""
                defaultValue={defaultValue || null}
              />
            )}
          </li>
        ))}
      </ul>
    </>
  );
};

export default FileUploader;
